import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { chatApi } from '../../api/chat';
import { ApiError } from '../../api/client';
import type { ConversationDetailView, MessagePage, MessageView, SendMessageInput } from '../../api/chat.types';
import { useChatRealtime } from '../../chat/ChatRealtimeContext';
import { ErrorState, LoadingState } from '../../components/ScreenState';
import { ProgressBar } from '../../ds';
import { useAsync } from '../../hooks/useAsync';
import { Avatar } from '../social/Avatar';
import { Composer } from '../social/Composer';
import { dayLabel } from '../social/format';
import { useSocial } from '../social/SocialContext';
import { SocialHeader } from '../social/SocialHeader';
import { SocialIcon, SocialIconButton } from '../social/SocialIcon';
import { ChatBubble } from './ChatBubble';
import { prepareImage } from './prepareImage';

const QUICK_REPLIES = ['Đã ôn xong 20 thẻ', 'Kiểm tra chéo nhé?', 'お疲れさま！'];
const hasKana = (text: string) => /[ぁ-ん]/.test(text);

/// Polling dự phòng — giống mobile. Vẫn chạy khi socket đang nối, chỉ thưa hơn: nếu API mất kết nối
/// Postgres của socket adapter, tin phát ở instance Cloud Run khác không tới được socket này.
const POLL_CONNECTED_MS = 15000;
const POLL_DISCONNECTED_MS = 5000;

/// Cách đáy dưới mức này thì coi như đang đọc tin mới nhất — có tin mới sẽ tự cuộn xuống.
const STICK_BOTTOM_PX = 80;
const LOAD_OLDER_TOP_PX = 120;

const tabVisible = () => document.visibilityState === 'visible';

/**
 * SCR-CHAT-02 trong khung bên phải trang Chat. Dùng chung cho DM và nhóm.
 * Cha đặt `key={conversationId}` để đổi hội thoại là dựng lại từ đầu.
 */
export function ChatThreadPane({
  conversationId,
  onBack,
  onConversation,
  infoActive,
  onToggleInfo,
}: {
  conversationId: string;
  /** Chỉ điện thoại: quay về danh sách. */
  onBack?: () => void;
  /** Báo chi tiết hội thoại cho panel "Thông tin nhóm". */
  onConversation?: (c: ConversationDetailView) => void;
  infoActive?: boolean;
  onToggleInfo?: () => void;
}) {
  const { me, toast } = useSocial();
  const { connected, setUnread, subscribe } = useChatRealtime();
  const [reloadKey, setReloadKey] = useState(0);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendingImage, setSendingImage] = useState(false);

  const conversation = useAsync(() => chatApi.getConversation(conversationId), [conversationId, reloadKey]);
  const firstPage = useAsync(async () => {
    const page = await chatApi.getMessages(conversationId);
    setUnread((await chatApi.markRead(conversationId)).unread);
    return page;
  }, [conversationId, reloadKey]);

  useEffect(() => {
    if (conversation.data) onConversation?.(conversation.data);
  }, [conversation.data, onConversation]);

  // `messages` mới nhất trước (đúng thứ tự API trả) — khi vẽ thì đảo lại.
  const [page, setPage] = useState<MessagePage | null>(null);
  const [messages, setMessages] = useState<MessageView[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  if (firstPage.data && firstPage.data !== page) {
    setPage(firstPage.data);
    setMessages(firstPage.data.items);
    setCursor(firstPage.data.nextCursor);
  }

  /// Thêm tin chưa có vào đầu list. Cùng một tin có thể về từ 3 ngả (response khi gửi, socket, polling).
  const merge = useCallback((incoming: MessageView[]) => {
    setMessages((current) => {
      const known = new Set(current.map((m) => m.id));
      const fresh = incoming.filter((m) => !known.has(m.id));
      return fresh.length ? [...fresh, ...current] : current;
    });
  }, []);

  const meId = me?.id;
  // Tab trình duyệt đang ẩn thì tin mới vẫn phải tính là chưa đọc.
  const markRead = useCallback(() => {
    if (tabVisible()) chatApi.markRead(conversationId).then(({ unread }) => setUnread(unread), () => undefined);
  }, [conversationId, setUnread]);

  useEffect(() => {
    document.addEventListener('visibilitychange', markRead);
    return () => document.removeEventListener('visibilitychange', markRead);
  }, [markRead]);

  useEffect(
    () =>
      subscribe((message) => {
        if (message.conversationId !== conversationId) return;
        merge([message]);
        if (message.senderId !== meId) markRead();
      }),
    [conversationId, meId, markRead, merge, subscribe],
  );

  useEffect(() => {
    const timer = setInterval(
      () => {
        chatApi.getMessages(conversationId).then(
          (latest) => {
            merge(latest.items);
            if (latest.items[0] && latest.items[0].senderId !== meId) markRead();
          },
          () => undefined, // Lỗi mạng tạm thời: lượt sau thử lại, không làm phiền user.
        );
      },
      connected ? POLL_CONNECTED_MS : POLL_DISCONNECTED_MS,
    );
    return () => clearInterval(timer);
  }, [connected, conversationId, meId, markRead, merge]);

  /* ---------- cuộn ---------- */
  const scroller = useRef<HTMLDivElement | null>(null);
  const stickToBottom = useRef(true);
  /// scrollHeight ngay trước khi chèn tin cũ lên đầu — để giữ nguyên chỗ đang đọc.
  const anchor = useRef<number | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const ordered = useMemo(() => [...messages].reverse(), [messages]);

  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    if (anchor.current != null) {
      el.scrollTop += el.scrollHeight - anchor.current;
      anchor.current = null;
    } else if (stickToBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [ordered]);

  const loadOlder = async () => {
    const el = scroller.current;
    if (!cursor || loadingOlder || !el) return;
    setLoadingOlder(true);
    try {
      const older = await chatApi.getMessages(conversationId, cursor);
      anchor.current = el.scrollHeight;
      setMessages((current) => [...current, ...older.items]);
      setCursor(older.nextCursor);
    } catch {
      toast('Không tải được tin cũ hơn');
    } finally {
      setLoadingOlder(false);
    }
  };

  const send = async (input: SendMessageInput) => {
    setSending(true);
    try {
      const sent = await chatApi.sendMessage(conversationId, input);
      stickToBottom.current = true;
      merge([sent]);
      return true;
    } catch {
      toast('Chưa gửi được tin nhắn, thử lại sau');
      return false;
    } finally {
      setSending(false);
    }
  };

  const sendText = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    if (await send({ text: trimmed })) setText('');
  };

  const sendImage = async (file: File) => {
    if (sendingImage) return;
    let image;
    try {
      image = await prepareImage(file);
    } catch {
      toast('Không đọc được ảnh này, thử ảnh JPG hoặc PNG');
      return;
    }
    setSendingImage(true);
    try {
      const sent = await chatApi.sendImage(conversationId, image);
      stickToBottom.current = true;
      merge([sent]);
    } catch (error) {
      // 503 = server chưa cấu hình nơi lưu ảnh — câu của server đã là tiếng Việt.
      toast(error instanceof ApiError && error.status === 503 ? error.message : 'Chưa gửi được ảnh, thử lại sau');
    } finally {
      setSendingImage(false);
    }
  };

  const c = conversation.data;
  const group = c?.kind === 'GROUP';
  const sub = !c ? undefined : group ? `${c.memberCount} thành viên${c.onlineCount ? ` · ${c.onlineCount} đang online` : ''}` : c.peer?.online ? 'Đang hoạt động' : c.peer?.level;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, background: 'var(--surface-page)' }}>
      <SocialHeader
        title={c?.name ?? ''}
        sub={sub}
        onBack={onBack}
        left={
          c && (
            <div style={{ marginRight: 10, marginLeft: onBack ? 0 : 8 }}>
              <Avatar name={c.name} size={38} mark={c.mark} online={!group && !!c.peer?.online} tone={group ? 0 : undefined} />
            </div>
          )
        }
        right={group && onToggleInfo ? <SocialIconButton name="group" label="Thông tin nhóm" active={infoActive} onClick={onToggleInfo} /> : undefined}
      />

      {group && c.weeklyGoal != null && (
        <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border-default)', background: 'var(--surface-tint)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <SocialIcon name="target" size={20} color="var(--cta-default)" />
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
              <span>{`Mục tiêu tuần: ${c.weeklyGoal} thẻ SRS`}</span>
              <span style={{ color: 'var(--text-secondary)' }}>{`${c.goalProgress ?? 0}/${c.weeklyGoal}`}</span>
            </div>
            <ProgressBar value={c.goalProgress ?? 0} max={c.weeklyGoal} />
          </div>
        </div>
      )}

      {firstPage.loading && !page ? (
        <LoadingState />
      ) : firstPage.error && !page ? (
        <ErrorState message="Không tải được tin nhắn" onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <>
          <div
            ref={scroller}
            onScroll={(e) => {
              const el = e.currentTarget;
              stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_BOTTOM_PX;
              if (el.scrollTop < LOAD_OLDER_TOP_PX) void loadOlder();
            }}
            style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}
          >
            {loadingOlder && <div style={{ alignSelf: 'center', fontSize: 12, color: 'var(--text-secondary)' }}>Đang tải tin cũ hơn…</div>}
            {ordered.length === 0 && (
              <div style={{ margin: 'auto', textAlign: 'center', fontSize: 14, color: 'var(--text-secondary)', maxWidth: 280 }}>
                {group ? 'Nhóm chưa có tin nào. Chào mọi người một câu nhé!' : 'Hai bạn chưa nhắn gì. Gửi lời chào đầu tiên nhé!'}
              </div>
            )}
            {ordered.map((m, i) => {
              const prev = ordered[i - 1];
              const newDay = !prev || dayLabel(prev.createdAt) !== dayLabel(m.createdAt);
              return (
                <div key={m.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {newDay && <div style={{ alignSelf: 'center', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', margin: '4px 0' }}>{dayLabel(m.createdAt)}</div>}
                  <ChatBubble message={m} mine={m.senderId === meId} group={group} showSender={newDay || prev.senderId !== m.senderId} />
                </div>
              );
            })}
          </div>
          {group && (
            <div className="sk-scroll-x" style={{ display: 'flex', gap: 8, padding: '4px 12px 0', overflowX: 'auto', flexShrink: 0 }}>
              {QUICK_REPLIES.map((reply) => (
                // Viên thuốc cao 36 như thiết kế, vùng bấm cao 44.
                <button
                  key={reply}
                  type="button"
                  disabled={sending}
                  onClick={() => send({ text: reply })}
                  style={{ flexShrink: 0, minHeight: 44, padding: 0, border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                >
                  <span
                    className="sk-press-page"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      height: 36,
                      padding: '0 14px',
                      borderRadius: 999,
                      border: '1px solid var(--border-default)',
                      fontFamily: hasKana(reply) ? 'var(--font-jp)' : 'var(--font-vi)',
                      fontSize: 13,
                      fontWeight: 600,
                      color: 'var(--ink)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {reply}
                  </span>
                </button>
              ))}
            </div>
          )}
          {sendingImage && (
            <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 16px 0', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
              <span style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid var(--border-default)', borderTopColor: 'var(--cta-default)', animation: 'torii-spin 0.8s linear infinite' }} />
              Đang gửi ảnh…
            </div>
          )}
          <Composer
            value={text}
            onChange={setText}
            onSend={sendText}
            sending={sending}
            onPickImage={sendImage}
            placeholder={group ? 'Nhắn cho nhóm…' : 'Nhắn tin…'}
          />
        </>
      )}
    </div>
  );
}
